import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIES_ENABLED } from '@/lib/config';
import { fetchSeriesById, generateSeriesEpisode } from '@/features/series/api';
import type { Series } from '@/features/series/types';
import type { StoryDetail } from '@/features/story/types';

export interface SeriesEpisodeContext {
  seriesId: string;
  seasonTitle: string;
  episodeNumber: number;
  total: number;
  isLast: boolean;
  /** True when the immediately following episode (episodeNumber + 1) already
   *  has a generated story — drives hiding "Nächste Folge erstellen" (#6). */
  nextEpisodeGenerated: boolean;
  worldSlug: string;
  generatingNext: boolean;
  onNextEpisode: () => void;
  onToSeason: () => void;
  onNewSeason: () => void;
}

/**
 * Resolves series-episode context for the story output page. The story
 * payload carries seriesId + episodeNumber but NOT the total / season
 * title, so we fetch GET /series/:id (GAP 1 resolution, PWA-only) when the
 * story belongs to a series AND the feature is build-enabled. Returns null
 * for non-series stories or when the feature is dark.
 */
export function useSeriesEpisodeContext(
  story: StoryDetail,
  worldSlug: string,
): SeriesEpisodeContext | null {
  const navigate = useNavigate();
  const seriesId = story.seriesId;
  const [series, setSeries] = useState<Series | null>(null);
  const [generatingNext, setGeneratingNext] = useState(false);

  useEffect(() => {
    if (!SERIES_ENABLED || !seriesId) return;
    let active = true;
    fetchSeriesById(seriesId)
      .then((s) => {
        if (active) setSeries(s);
      })
      .catch(() => {
        /* breadcrumb/CTAs are best-effort; fall back to default footer */
      });
    return () => {
      active = false;
    };
  }, [seriesId]);

  if (!SERIES_ENABLED || !seriesId || series === null || story.episodeNumber === null) {
    return null;
  }

  const total = series.episodeCount;
  const episodeNumber = story.episodeNumber;
  const isLast = episodeNumber >= total;
  const nextEpisodeGenerated = series.episodes.some(
    (e) => e.episodeNumber === episodeNumber + 1 && Boolean(e.storyId),
  );

  async function onNextEpisode() {
    if (generatingNext || seriesId === null) return;
    setGeneratingNext(true);
    try {
      const res = await generateSeriesEpisode(seriesId, episodeNumber + 1);
      if (res.storyJobId) {
        navigate(`/output/${res.storyJobId}`, { state: { world: worldSlug } });
        return;
      }
      if (res.storyId) {
        navigate(`/story/${res.storyId}`);
        return;
      }
      setGeneratingNext(false);
    } catch {
      setGeneratingNext(false);
    }
  }

  return {
    seriesId,
    seasonTitle: series.title ?? '',
    episodeNumber,
    total,
    isLast,
    nextEpisodeGenerated,
    worldSlug,
    generatingNext,
    onNextEpisode,
    onToSeason: () => navigate(`/series/${seriesId}`),
    onNewSeason: () => navigate(`/builder/${worldSlug}`),
  };
}
